import { motion } from 'framer-motion';
import { Search, Video, Zap, Flame } from 'lucide-react';

// Phone mockup component
function PhoneMockup({ screenshot, alt }: { screenshot: string; alt: string }) {
    return (
        <div className="relative w-48 sm:w-56">
            <div className="relative bg-black rounded-[2rem] p-1.5 shadow-2xl shadow-black/50">
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-20" />
                <div className="relative rounded-[1.5rem] overflow-hidden bg-dark-900">
                    <img src={screenshot} alt={alt} className="w-full h-auto" />
                </div>
            </div>
        </div>
    );
}

export default function HowItWorks() {
    const steps = [
        {
            number: 1,
            icon: Search,
            title: 'Browse Dares',
            description: 'Explore trending challenges by category - Comedy, Fitness, Talent, and more',
            screenshot: '/app-explore-dares.png',
            screenshotAlt: 'DareUp Explore Dares Screen',
        },
        {
            number: 2,
            icon: Video,
            title: 'Record Your Take',
            description: 'Use our built-in camera with creative tools and up to 5 minutes of recording',
            screenshot: '/app-record.png',
            screenshotAlt: 'DareUp Recording Screen',
        },
        {
            number: 3,
            icon: Zap,
            title: 'Go Viral',
            description: 'Share with the community, earn XP, climb leaderboards, and unlock achievements',
            screenshot: '/app-feed.png',
            screenshotAlt: 'DareUp Feed Screen',
        },
    ];

    return (
        <section className="py-24 gradient-bg overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-20"
                >
                    <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-primary-500/10 text-primary-400 rounded-full text-sm font-semibold border border-primary-500/20">
                        <Flame className="w-4 h-4" />
                        How It Works
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-4">
                        From Dare to Done in 60 Seconds
                    </h2>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Creating viral content has never been easier
                    </p>
                </motion.div>

                <div className="max-w-6xl mx-auto space-y-24">
                    {steps.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-100px' }}
                            transition={{ duration: 0.6 }}
                            className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-20`}
                        >
                            {/* Phone mockup */}
                            <div className="relative flex-shrink-0">
                                {/* Glow effect */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary-500/20 rounded-full blur-[80px]" />
                                <PhoneMockup screenshot={step.screenshot} alt={step.screenshotAlt} />
                            </div>

                            {/* Content */}
                            <div className={`flex-1 text-center ${index % 2 === 0 ? 'lg:text-left' : 'lg:text-right'}`}>
                                <div
                                    className={`inline-flex items-center gap-3 mb-6 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                                >
                                    <div className="flex items-center justify-center w-14 h-14 bg-primary-500 rounded-2xl shadow-lg shadow-primary-500/30">
                                        <step.icon className="w-7 h-7 text-white" />
                                    </div>
                                    <span className="text-5xl font-bold text-dark-700">0{step.number}</span>
                                </div>

                                <h3 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
                                    {step.title}
                                </h3>
                                <p className="text-lg text-gray-400 max-w-md mx-auto lg:mx-0">{step.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
